import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import useAiAudienceSearch, {
  DemographicResult,
  EsriResult,
  LiverampResult,
  SegmentResult,
} from '../../hooks/useAiAudienceSearch';
import useDebounce from '../../hooks/useDebounce';
import { ExternalSource } from '../../hooks/useResolveExternalSegment';
import posthog from '../../util/posthog';

export interface SelectedSegment {
  // externalSource/externalId are the pair expected by the backend resolve_external
  // endpoint (not always identical to the AI-search response group key; e.g.
  // `segments` resolves under externalSource `inmarket`).
  externalSource: ExternalSource;
  externalId: string;
  name: string;
}

// onUseSegment may resolve the selection asynchronously (e.g. resolving the AI
// result to a ventura segment). Returning { error } lets the consumer report a
// failure (such as an unresolvable result) that this component surfaces inline.
export interface UseSegmentResult {
  error?: string | null;
}

interface Props {
  onUseSegment?: (segment: SelectedSegment) => void | UseSegmentResult | Promise<void | UseSegmentResult>;
}

// Shared handler passed down to every section's Use button. It runs the
// consumer's onUseSegment (which may be async), shows a per-button spinner while
// pending, and reports any returned error back up to AiAudienceSearch for inline
// display. Sections render <UseButton segment={...} /> instead of a raw Button.
type UseHandler = (segment: SelectedSegment) => Promise<void>;
const UseHandlerContext = React.createContext<UseHandler | undefined>(undefined);

function UseButton({ segment }: { segment: SelectedSegment }) {
  const handleUse = React.useContext(UseHandlerContext);
  const [pending, setPending] = useState(false);
  if (!handleUse) return null;
  return (
    <Button
      size="small"
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await handleUse(segment);
        } finally {
          setPending(false);
        }
      }}
    >
      {pending ? <CircularProgress color="inherit" size={14} /> : 'Use'}
    </Button>
  );
}

function similarityColor(pct: number) {
  if (pct >= 80) return 'success';
  if (pct >= 70) return 'warning';
  return 'default';
}

function SimilarityChip({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = similarityColor(pct);
  return <Chip label={`${pct}%`} size="small" color={color} variant="outlined" />;
}

function ResultTable({ children }: { children: React.ReactNode }) {
  return (
    <Table size="small">
      {children}
    </Table>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for when Esri results are re-enabled
function EsriSection({ results, onUseSegment }: { results: EsriResult[]; onUseSegment?: Props['onUseSegment'] }) {
  if (results.length === 0) return <Typography variant="body2" color="text.secondary">No results</Typography>;
  return (
    <ResultTable>
      <TableHead>
        <TableRow>
          <TableCell width={130}>ID</TableCell>
          <TableCell>Description</TableCell>
          <TableCell width={70}>Match</TableCell>
          {onUseSegment && <TableCell width={60} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${r.audience_id}-${idx}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.audience_id}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{r.description}</Typography>
            </TableCell>
            <TableCell><SimilarityChip value={r.similarities} /></TableCell>
            {onUseSegment && (
              <TableCell>
                <UseButton segment={{ externalSource: 'esri', externalId: r.audience_id, name: r.description }} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </ResultTable>
  );
}

function SegmentsSection({ results, onUseSegment }: { results: SegmentResult[]; onUseSegment?: Props['onUseSegment'] }) {
  if (results.length === 0) return <Typography variant="body2" color="text.secondary">No results</Typography>;
  return (
    <ResultTable>
      <TableHead>
        <TableRow>
          <TableCell width={100}>ID</TableCell>
          <TableCell>Segment</TableCell>
          <TableCell width={70}>Match</TableCell>
          <TableCell width={60}>Price</TableCell>
          <TableCell width={100}>Size</TableCell>
          {onUseSegment && <TableCell width={60} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${r.audience_id}-${idx}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.audience_id}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{r.segment_name}</Typography>
              <Typography variant="caption" color="text.secondary">{r.description}</Typography>
            </TableCell>
            <TableCell><SimilarityChip value={r.similarities} /></TableCell>
            <TableCell>
              <Typography variant="body2">{r.segment_price || '—'}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{r.segment_size || '—'}</Typography>
            </TableCell>
            {onUseSegment && (
              <TableCell>
                <UseButton segment={{ externalSource: 'inmarket', externalId: r.audience_id, name: r.segment_name }} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </ResultTable>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for when Demographic results are re-enabled
function DemographicSection({ results, onUseSegment }: { results: DemographicResult[]; onUseSegment?: Props['onUseSegment'] }) {
  if (results.length === 0) return <Typography variant="body2" color="text.secondary">No results</Typography>;
  return (
    <ResultTable>
      <TableHead>
        <TableRow>
          <TableCell width={130}>ID</TableCell>
          <TableCell>Description</TableCell>
          {onUseSegment && <TableCell width={60} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${r.attribute_name}-${idx}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.attribute_name}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{r.description}</Typography>
            </TableCell>
            {onUseSegment && (
              <TableCell>
                <UseButton segment={{ externalSource: 'demographic', externalId: r.attribute_name, name: r.description }} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </ResultTable>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for when LiveRamp results are re-enabled
function LiverampSection({ results, onUseSegment }: { results: LiverampResult[]; onUseSegment?: Props['onUseSegment'] }) {
  if (results.length === 0) return <Typography variant="body2" color="text.secondary">No results</Typography>;
  return (
    <ResultTable>
      <TableHead>
        <TableRow>
          <TableCell width={110}>ID</TableCell>
          <TableCell>Description</TableCell>
          <TableCell width={70}>Match</TableCell>
          <TableCell width={60}>Price</TableCell>
          {onUseSegment && <TableCell width={60} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${r.segment_id}-${idx}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.segment_id}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{r.description}</Typography>
            </TableCell>
            <TableCell><SimilarityChip value={r.similarities} /></TableCell>
            <TableCell>
              <Typography variant="body2">{r.price != null ? `$${r.price.toFixed(2)}` : '—'}</Typography>
            </TableCell>
            {onUseSegment && (
              <TableCell>
                <UseButton segment={{ externalSource: 'liveramp', externalId: r.segment_id, name: r.description }} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </ResultTable>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
  );
}

function AiAudienceSearch({ onUseSegment }: Props) {
  const [inputValue, setInputValue] = useState('');
  const debouncedQuery = useDebounce(inputValue, 300);
  const hasQuery = debouncedQuery.trim().length > 0;
  const { data, isLoading, isError } = useAiAudienceSearch(debouncedQuery);
  const [useError, setUseError] = useState<string | null>(null);

  // Report each settled search once, so we can see how the feature is used:
  // what people search for, and how many results (per source) come back. Keyed
  // on the query + data identity so it fires per completed search, not per
  // render. Only the visible sources' counts are recorded distinctly, but all
  // are included for future analysis.
  React.useEffect(() => {
    if (!hasQuery || isLoading) return;
    if (data) {
      posthog.capture('ai_audience_search_completed', {
        query: debouncedQuery,
        result_count:
          data.segments.length +
          data.demographic.length +
          data.esri.length +
          (data.liveramp?.length ?? 0),
        segments_count: data.segments.length,
        demographic_count: data.demographic.length,
        esri_count: data.esri.length,
        liveramp_count: data.liveramp?.length ?? 0,
      });
    } else if (isError) {
      posthog.capture('ai_audience_search_failed', { query: debouncedQuery });
    }
    // debouncedQuery is the identity of the completed search; data/isError are
    // its outcome. We intentionally do not depend on hasQuery/isLoading beyond
    // the guard above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, data, isError]);

  // Wrap the consumer's onUseSegment so a returned/thrown error surfaces inline.
  // Awaiting here keeps the per-button spinner (UseButton) up until it settles.
  const handleUse = React.useCallback(
    async (segment: SelectedSegment) => {
      if (!onUseSegment) return;
      setUseError(null);
      // The "use" click is the key conversion for AI search: someone acting on a
      // suggested result. Captured before the (possibly async) resolve so the
      // intent is recorded even if the resolve fails.
      posthog.capture('ai_audience_use_clicked', {
        external_source: segment.externalSource,
        external_id: segment.externalId,
        query: debouncedQuery,
      });
      try {
        const result = await onUseSegment(segment);
        if (result && result.error) {
          setUseError(result.error);
          posthog.capture('ai_audience_use_failed', {
            external_source: segment.externalSource,
            external_id: segment.externalId,
            reason: result.error,
          });
        } else {
          posthog.capture('ai_audience_use_succeeded', {
            external_source: segment.externalSource,
            external_id: segment.externalId,
          });
        }
      } catch {
        setUseError('Could not use this result. Please try again.');
        posthog.capture('ai_audience_use_failed', {
          external_source: segment.externalSource,
          external_id: segment.externalId,
          reason: 'exception',
        });
      }
    },
    [onUseSegment, debouncedQuery]
  );

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Describe your audience (e.g. female tennis players)"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        sx={{ backgroundColor: 'white' }}
        InputProps={{
          endAdornment: hasQuery && isLoading ? <CircularProgress color="inherit" size={16} /> : undefined,
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          AI search is currently unavailable. The name filter above still works.
        </Alert>
      )}

      {hasQuery && isLoading && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {[...Array(3)].map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={idx} animation="wave" variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      )}

      {useError && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setUseError(null)}>
          {useError}
        </Alert>
      )}

      {data && !isLoading && (
        <UseHandlerContext.Provider value={handleUse}>
          <Box sx={{ mt: 2 }}>
            {data.elaborated_audience_description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Interpreted as: {data.elaborated_audience_description}
              </Typography>
            )}
            <Stack spacing={2} divider={<Divider />}>
              {/* Only Syndicated Segments are surfaced for now. The other
                  sources below are wired up but hidden until they're ready to
                  be resolved to ventura segments; uncomment to re-enable. */}
              {/* <Box>
                <SectionHeader title="Demographic" />
                <DemographicSection results={data.demographic} onUseSegment={onUseSegment} />
              </Box>
              <Box>
                <SectionHeader title="Esri" />
                <EsriSection results={data.esri} onUseSegment={onUseSegment} />
              </Box> */}
              <Box>
                <SectionHeader title="Syndicated Segments" />
                <SegmentsSection results={data.segments} onUseSegment={onUseSegment} />
              </Box>
              {/* {data.liveramp && data.liveramp.length > 0 && (
                <Box>
                  <SectionHeader title="LiveRamp" />
                  <LiverampSection results={data.liveramp} onUseSegment={onUseSegment} />
                </Box>
              )} */}
            </Stack>
          </Box>
        </UseHandlerContext.Provider>
      )}
    </Box>
  );
}

export default AiAudienceSearch;
